import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Menu as MuiMenu,
  MenuItem,
  MenuList,
  Typography,
} from "@mui/material";
import {
  Close,
  FormatListBulleted,
  KeyboardArrowDownRounded,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { FiSearch } from "react-icons/fi";
import axios from "axios";
import UpgradeIcon from "assets/icons/upgrade.svg";
import LeaderboardIcon from "assets/images/icons/leaderboard.svg";
import ChallengeIcon from "assets/images/icons/challenge_icon.svg";
import GamesIcon from "assets/images/icons/games_icon.svg";
import QuizBattleIcon from "assets/images/icons/quizbattle.svg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { useReduxUserProfile } from "Utils/hooks";
import {
  filterBySearchQuery,
  getKeywordLabel,
  getKeywordSearchFields,
  getListSearchFields,
  getSubjectLabel,
  getTopicLabel,
  getUserSearchFields,
  normalizeCollection,
  normalizePersonRecord,
} from "Utils/globalSearch";
import { getMinorSafeDisplayName, isMinorProfile } from "Utils/minorSafety";
import { userLogout } from "Redux/Actions";
import { useDiscoverUsers, useGlobalSearchCatalog } from "Utils/queries";
import { resolveMediaUrlsBulk } from "Utils/mediaUpload";
import { getToken } from "Shared/baseURL";
import NotificationPopover from "Components/Notifications/NotificationPopover";
import TrialStatusChip from "Components/TrialStatus/TrialStatusChip";
import TrialStatusPopover from "Components/TrialStatus/TrialStatusPopover";
import useTrialStatus from "hooks/useTrialStatus";
import { runWithGameLeaveGuard } from "Pages/Games/gameLeaveGuardRegistry";

const NAV_ITEMS = [
  {
    label: "List",
    path: "/lists",
    className: "nav-pill--user-section",
    isActive: (pathname) =>
      pathname === "/lists" ||
      pathname.startsWith("/lists/") ||
      pathname.startsWith("/view-list"),
  },
  {
    label: "Playground",
    type: "menu",
    className: "nav-pill--user-section",
    isActive: (pathname) =>
      pathname.startsWith("/games") || pathname.startsWith("/challenge-hub"),
  },
  {
    label: "My Network",
    path: "/my-network",
    className: "nav-pill--user-section",
    isActive: (pathname) => pathname.startsWith("/my-network"),
  },
];

const PLAYGROUND_ITEMS = [
  {
    label: "Games",
    path: "/games",
    icon: GamesIcon,
  },
  {
    label: "Challenges",
    path: "/challenge-hub",
    icon: ChallengeIcon,
  },
  {
    label: "Quiz Battles",
    path: "/quiz-battles",
    icon: QuizBattleIcon,
    comingSoon: true,
    badge: "SOON",
  },
];

const SEARCH_PREVIEW_MIN_QUERY_LENGTH = 2;
const SEARCH_PREVIEW_LIMITS = {
  people: 3,
  lists: 3,
  keywords: 4,
};

const useDebouncedValue = (value, delay = 350) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebounced(value);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [value, delay]);

  return debounced;
};

const getFirstAuthor = (author) => {
  if (Array.isArray(author)) {
    return author[0] || null;
  }

  return author || null;
};

const getSearchPreviewDisplayName = (user) => {
  const isMinorUser = isMinorProfile(user);
  if (isMinorUser) {
    return (
      getMinorSafeDisplayName(user, {
        fallbackUsername:
          user?.username ||
          user?.userName ||
          user?.preferredUsername ||
          user?.preferred_username,
      }) ||
      user?.firstName ||
      user?.username ||
      "User"
    );
  }

  return user?.displayName || user?.username || "User";
};

const resolveSearchPreviewAvatar = (user, mediaUrlMap = {}) => {
  const mediaId = String(
    user?.avatarId ||
      user?.avatarID ||
      user?.avatar?.mediaId ||
      user?.avatar?.id ||
      user?.profileImage?.mediaId ||
      user?.profileImage?.id ||
      user?.profile?.avatarId ||
      user?.profile?.avatarID ||
      user?.profile?.avatar?.mediaId ||
      user?.profile?.avatar?.id ||
      "",
  ).trim();
  const candidates = [
    user?.avatarURL,
    user?.avatarUrl,
    user?.avatar,
    user?.photoUrl,
    user?.picture,
    user?.profileImageUrl,
    user?.profileImage,
    user?.imageUrl,
    user?.image,
    user?.profile?.avatarURL,
    user?.profile?.avatarUrl,
    user?.profile?.avatar,
    user?.profile?.profileImageUrl,
  ];

  return (
    candidates
      .map((value) => {
        if (typeof value === "string") return value.trim();
        if (value && typeof value === "object") {
          return String(value.url || value.imageUrl || value.src || "").trim();
        }
        return "";
      })
      .find((value) => value.length > 0 && value !== "null") ||
    (mediaId ? mediaUrlMap[mediaId] : "") ||
    ""
  );
};

const buildSearchResultsUrl = (query, category) => {
  const params = new URLSearchParams();
  const trimmedQuery = String(query || "").trim();

  if (trimmedQuery) {
    params.set("q", trimmedQuery);
  }

  if (category) {
    params.set("category", category);
  }

  const queryString = params.toString();
  return queryString ? `/search-results?${queryString}` : "/search-results";
};

const buildListViewUrl = (subjectHref, listHref, keywordId = null) => {
  const normalizedSubjectHref = String(subjectHref || "").trim();
  const normalizedListHref = String(listHref || "").trim();

  if (!normalizedSubjectHref || !normalizedListHref) {
    return "";
  }

  const params = new URLSearchParams();
  if (keywordId != null && String(keywordId).trim()) {
    params.set("keyword", String(keywordId).trim());
  }

  const queryString = params.toString();
  return `/view-list/${normalizedSubjectHref}/${normalizedListHref}${
    queryString ? `?${queryString}` : ""
  }`;
};

const isEmailLike = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const resolveProfileRouteKey = (user) => {
  const id = String(user?.id || user?.userId || "").trim();
  const userId = String(user?.userId || "").trim();
  const uuid = String(user?.uuid || "").trim();
  const publicId = String(user?.publicId || "").trim();
  const primaryUsername = String(user?.username || user?.userName || "").trim();
  const preferredUsername = String(user?.preferredUsername || "").trim();

  if (id) {
    return id;
  }

  if (userId) {
    return userId;
  }

  if (uuid) {
    return uuid;
  }

  if (publicId) {
    return publicId;
  }

  if (primaryUsername && !isEmailLike(primaryUsername)) {
    return primaryUsername;
  }

  if (preferredUsername && !isEmailLike(preferredUsername)) {
    return preferredUsername;
  }

  return primaryUsername || preferredUsername || publicId;
};

const resolveProfileDisplaySlug = (user) => {
  return resolveProfileRouteKey(user);
};

const getSearchPreviewUserIdentifiers = (user) =>
  [
    user?.id,
    user?.userId,
    user?.uuid,
    user?.publicId,
    user?.username,
    user?.userName,
    user?.preferredUsername,
    user?.preferred_username,
  ]
    .filter((value) => value != null && String(value).trim())
    .map((value) => String(value).trim());

const mergeSearchPreviewUserDetails = (user, detail) => {
  if (!user || !detail) return user;
  const cached = normalizePersonRecord(detail) || detail;

  return {
    ...cached,
    ...user,
    profile: {
      ...(cached?.profile || {}),
      ...(user?.profile || {}),
    },
    avatarURL:
      user?.avatarURL ||
      user?.avatarUrl ||
      cached?.avatarURL ||
      cached?.avatarUrl ||
      cached?.avatar ||
      cached?.profileImageUrl ||
      cached?.profile?.avatarURL ||
      cached?.profile?.avatarUrl ||
      "",
    avatarUrl:
      user?.avatarUrl ||
      user?.avatarURL ||
      cached?.avatarUrl ||
      cached?.avatarURL ||
      cached?.avatar ||
      cached?.profileImageUrl ||
      cached?.profile?.avatarUrl ||
      cached?.profile?.avatarURL ||
      "",
    avatarId:
      user?.avatarId ||
      user?.avatarID ||
      user?.avatar?.mediaId ||
      user?.avatar?.id ||
      user?.profileImage?.mediaId ||
      user?.profileImage?.id ||
      cached?.avatarId ||
      cached?.avatarID ||
      cached?.avatar?.mediaId ||
      cached?.avatar?.id ||
      cached?.profileImage?.mediaId ||
      cached?.profileImage?.id ||
      cached?.profile?.avatarId ||
      cached?.profile?.avatarID ||
      cached?.profile?.avatar?.mediaId ||
      cached?.profile?.avatar?.id ||
      "",
    avatarID:
      user?.avatarID ||
      user?.avatarId ||
      user?.avatar?.mediaId ||
      user?.avatar?.id ||
      user?.profileImage?.mediaId ||
      user?.profileImage?.id ||
      cached?.avatarID ||
      cached?.avatarId ||
      cached?.avatar?.mediaId ||
      cached?.avatar?.id ||
      cached?.profileImage?.mediaId ||
      cached?.profileImage?.id ||
      cached?.profile?.avatarID ||
      cached?.profile?.avatarId ||
      cached?.profile?.avatar?.mediaId ||
      cached?.profile?.avatar?.id ||
      "",
  };
};

const UserHeader = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [playgroundAnchorEl, setPlaygroundAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobilePlaygroundOpen, setMobilePlaygroundOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dismissedSearchOptionIds, setDismissedSearchOptionIds] = useState(
    () => new Set(),
  );
  const [searchAvatarMediaUrlMap, setSearchAvatarMediaUrlMap] = useState({});
  const [searchPeopleDetailCache, setSearchPeopleDetailCache] = useState({});

  const dispatch = useDispatch();
  const profile = useReduxUserProfile();
  const trial = useTrialStatus(profile);
  const [trialPopoverAnchor, setTrialPopoverAnchor] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const searchContainerRef = useRef(null);
  const playgroundCloseTimerRef = useRef(null);
  const fetchedSearchPeopleDetailRef = useRef(new Set());

  const guardedNavigate = useCallback(
    (to, options, afterNavigate) => {
      runWithGameLeaveGuard(() => {
        navigate(to, options);
        afterNavigate?.();
      });
    },
    [navigate],
  );

  useEffect(
    () => () => {
      if (playgroundCloseTimerRef.current) {
        window.clearTimeout(playgroundCloseTimerRef.current);
      }
    },
    [],
  );

  const queryQ = useMemo(() => {
    if (!location.search) return "";
    return new URLSearchParams(location.search).get("q") || "";
  }, [location.search]);

  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebouncedValue(trimmedSearchTerm);
  const shouldSearchSuggestions =
    (isSearchFocused || isSearchDropdownOpen) &&
    trimmedSearchTerm.length >= SEARCH_PREVIEW_MIN_QUERY_LENGTH;
  const discoverUsersRQ = useDiscoverUsers({
    enabled: shouldSearchSuggestions,
    query: debouncedSearchTerm,
    size: 60,
  });
  const globalSearchCatalogRQ = useGlobalSearchCatalog({
    enabled: shouldSearchSuggestions,
  });

  const previewPeopleSource = useMemo(
    () =>
      normalizeCollection(discoverUsersRQ?.data)
        .map((item) => normalizePersonRecord(item))
        .filter(Boolean),
    [discoverUsersRQ?.data],
  );

  useEffect(() => {
    if (!shouldSearchSuggestions || previewPeopleSource.length === 0)
      return undefined;

    const peopleToFetch = previewPeopleSource
      .map((person) => {
        const identifiers = getSearchPreviewUserIdentifiers(person);
        const cacheKey = identifiers[0] || "";
        return { identifiers, cacheKey };
      })
      .filter(
        ({ identifiers, cacheKey }) =>
          identifiers.length > 0 &&
          cacheKey &&
          !searchPeopleDetailCache[cacheKey] &&
          !fetchedSearchPeopleDetailRef.current.has(cacheKey),
      );

    if (!peopleToFetch.length) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;

        const results = await Promise.allSettled(
          peopleToFetch.map(async ({ identifiers, cacheKey }) => {
            for (const identifier of identifiers) {
              try {
                const response = await axios.get(
                  `/sns/v1/users/${identifier}`,
                  {
                    headers: { Authorization: token },
                  },
                );
                return { cacheKey, identifiers, data: response.data };
              } catch {
                continue;
              }
            }

            throw new Error(`Unable to fetch user details for ${cacheKey}`);
          }),
        );

        if (cancelled) return;

        const nextCache = {};
        results.forEach((result) => {
          if (result.status !== "fulfilled" || !result.value?.data) return;
          const { cacheKey, identifiers, data } = result.value;
          const normalized = normalizePersonRecord(data) || data;
          identifiers.forEach((identifier) => {
            nextCache[identifier] = normalized;
            fetchedSearchPeopleDetailRef.current.add(identifier);
          });
          nextCache[cacheKey] = normalized;
          fetchedSearchPeopleDetailRef.current.add(cacheKey);
        });

        if (Object.keys(nextCache).length) {
          setSearchPeopleDetailCache((prev) => ({ ...prev, ...nextCache }));
        }
      } catch {
        /* Search suggestions can still render with the sparse payload. */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [previewPeopleSource, searchPeopleDetailCache, shouldSearchSuggestions]);

  const enrichedPreviewPeopleSource = useMemo(
    () =>
      previewPeopleSource.map((person) => {
        const identifiers = getSearchPreviewUserIdentifiers(person);
        const detail = identifiers
          .map((identifier) => searchPeopleDetailCache[identifier])
          .find(Boolean);
        return detail ? mergeSearchPreviewUserDetails(person, detail) : person;
      }),
    [previewPeopleSource, searchPeopleDetailCache],
  );

  useEffect(() => {
    const mediaIds = Array.from(
      new Set(
        enrichedPreviewPeopleSource
          .map((user) => String(user?.avatarId || user?.avatarID || "").trim())
          .filter(Boolean),
      ),
    ).filter((mediaId) => !searchAvatarMediaUrlMap[mediaId]);

    if (!mediaIds.length) return undefined;

    let cancelled = false;
    resolveMediaUrlsBulk({ mediaIds, ttl: 3600 })
      .then((resolved) => {
        if (cancelled || !resolved || Object.keys(resolved).length === 0)
          return;
        setSearchAvatarMediaUrlMap((prev) => ({ ...prev, ...resolved }));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [enrichedPreviewPeopleSource, searchAvatarMediaUrlMap]);

  const previewListsSource = useMemo(
    () => normalizeCollection(globalSearchCatalogRQ?.data?.lists || []),
    [globalSearchCatalogRQ?.data?.lists],
  );

  const previewKeywordsSource = useMemo(
    () => normalizeCollection(globalSearchCatalogRQ?.data?.keywords || []),
    [globalSearchCatalogRQ?.data?.keywords],
  );

  const peoplePreviewItems = useMemo(
    () =>
      filterBySearchQuery(
        enrichedPreviewPeopleSource,
        debouncedSearchTerm,
        getUserSearchFields,
      )
        .slice(0, SEARCH_PREVIEW_LIMITS.people)
        .map((user, index) => {
          const displayName = getSearchPreviewDisplayName(user);
          const profileRouteKey = resolveProfileRouteKey(user);
          const profileDisplaySlug = resolveProfileDisplaySlug(user);
          const secondaryText = [
            user?.username ? `@${user.username}` : "",
            user?.highestQualification ||
              user?.studiesAt ||
              user?.department ||
              user?.organizationName ||
              "",
          ]
            .filter(Boolean)
            .join(" | ");

          return {
            id: `people-${user?.uuid || user?.id || user?.username || index}`,
            category: "people",
            type: "person",
            label: displayName,
            subLabel: secondaryText,
            queryValue: trimmedSearchTerm,
            avatarURL: resolveSearchPreviewAvatar(
              user,
              searchAvatarMediaUrlMap,
            ),
            navigateTo: profileDisplaySlug
              ? `/my-network/${profileDisplaySlug}`
              : "",
            routeState: profileRouteKey
              ? { userId: profileRouteKey }
              : undefined,
          };
        }),
    [
      debouncedSearchTerm,
      enrichedPreviewPeopleSource,
      searchAvatarMediaUrlMap,
      trimmedSearchTerm,
    ],
  );

  const listsPreviewItems = useMemo(
    () =>
      filterBySearchQuery(
        previewListsSource,
        debouncedSearchTerm,
        getListSearchFields,
      )
        .slice(0, SEARCH_PREVIEW_LIMITS.lists)
        .map((listItem, index) => {
          const author = getFirstAuthor(listItem?.author);
          const subjectLabel = getSubjectLabel(listItem?.subject);
          const topicLabel =
            getTopicLabel(listItem?.topic) ||
            getTopicLabel(listItem?.topics) ||
            getTopicLabel(listItem?.category);
          const label =
            listItem?.name ||
            listItem?.title ||
            listItem?.listName ||
            topicLabel ||
            subjectLabel ||
            "List";
          const subLabel = [
            subjectLabel ? `Subject: ${subjectLabel}` : "",
            topicLabel ? `Topic: ${topicLabel}` : "",
            author?.username ? `by @${author.username}` : "",
          ]
            .filter(Boolean)
            .join(" | ");

          return {
            id: `list-${listItem?.uuid || listItem?.id || listItem?.href || index}`,
            category: "lists",
            type: "list",
            label,
            subLabel,
            queryValue: trimmedSearchTerm,
            navigateTo: buildListViewUrl(
              listItem?.subjectHref ||
                listItem?.subject?.href ||
                listItem?.subject?.id ||
                listItem?.subjectId,
              listItem?.href || listItem?.uuid || listItem?.id,
            ),
          };
        }),
    [debouncedSearchTerm, previewListsSource, trimmedSearchTerm],
  );

  const keywordsPreviewItems = useMemo(
    () =>
      filterBySearchQuery(
        previewKeywordsSource,
        debouncedSearchTerm,
        getKeywordSearchFields,
      )
        .slice(0, SEARCH_PREVIEW_LIMITS.keywords)
        .map((keyword, index) => {
          const subjectLabel = getSubjectLabel(keyword?.subject);
          const topicLabel =
            getTopicLabel(keyword?.topic) ||
            getTopicLabel(keyword?.topics) ||
            getTopicLabel(keyword?.category);
          const listLabel =
            keyword?.listName ||
            keyword?.listTitle ||
            keyword?.list?.name ||
            keyword?.list?.title ||
            "";
          const label =
            getKeywordLabel(keyword) || topicLabel || subjectLabel || "Keyword";

          return {
            id: `keyword-${keyword?.uuid || keyword?.id || label || index}`,
            category: "lists",
            type: "keyword",
            label,
            subLabel:
              [
                listLabel ? `List: ${listLabel}` : "",
                subjectLabel ? `Subject: ${subjectLabel}` : "",
                topicLabel ? `Topic: ${topicLabel}` : "",
              ]
                .filter(Boolean)
                .join(" | ") || "Keyword",
            queryValue: trimmedSearchTerm,
            navigateTo: buildListViewUrl(
              keyword?.subject?.href ||
                keyword?.subjectHref ||
                keyword?.subject?.id ||
                keyword?.subjectId,
              keyword?.listId || keyword?.list?.id || keyword?.list?.href,
              keyword?.uuid || keyword?.id,
            ),
          };
        }),
    [debouncedSearchTerm, previewKeywordsSource, trimmedSearchTerm],
  );

  const searchPreviewSections = useMemo(
    () =>
      [
        {
          key: "people",
          title: "People",
          items: peoplePreviewItems,
        },
        {
          key: "lists",
          title: "Lists",
          items: listsPreviewItems,
        },
        {
          key: "keywords",
          title: "Keywords",
          items: keywordsPreviewItems,
        },
      ].filter((section) => section.items.length > 0),
    [keywordsPreviewItems, listsPreviewItems, peoplePreviewItems],
  );

  const footerSearchOption = useMemo(() => {
    if (trimmedSearchTerm.length < SEARCH_PREVIEW_MIN_QUERY_LENGTH) {
      return null;
    }

    return {
      id: "all-results",
      category: null,
      type: "all",
      label: "View all results",
      queryValue: trimmedSearchTerm,
    };
  }, [trimmedSearchTerm]);

  const visibleSearchPreviewSections = useMemo(
    () =>
      searchPreviewSections
        .map((section) => ({
          ...section,
          items: section.items.filter(
            (item) => !dismissedSearchOptionIds.has(item.id),
          ),
        }))
        .filter((section) => section.items.length > 0),
    [dismissedSearchOptionIds, searchPreviewSections],
  );

  const flattenedSearchOptions = useMemo(() => {
    const options = visibleSearchPreviewSections.flatMap(
      (section) => section.items,
    );

    if (footerSearchOption) {
      options.push(footerSearchOption);
    }

    return options;
  }, [footerSearchOption, visibleSearchPreviewSections]);

  const searchOptionIndexMap = useMemo(() => {
    const nextMap = new Map();
    flattenedSearchOptions.forEach((option, index) => {
      nextMap.set(option.id, index);
    });
    return nextMap;
  }, [flattenedSearchOptions]);

  const showSearchDropdown =
    isSearchDropdownOpen &&
    trimmedSearchTerm.length >= SEARCH_PREVIEW_MIN_QUERY_LENGTH;
  const isSearchPreviewLoading =
    showSearchDropdown &&
    (trimmedSearchTerm !== debouncedSearchTerm ||
      discoverUsersRQ.isLoading ||
      globalSearchCatalogRQ.isLoading ||
      discoverUsersRQ.isFetching ||
      globalSearchCatalogRQ.isFetching);
  const showNoSearchMatches =
    showSearchDropdown &&
    !isSearchPreviewLoading &&
    visibleSearchPreviewSections.length === 0;

  useEffect(() => {
    if (location.pathname.startsWith("/search-results")) {
      setSearchTerm(queryQ);
    } else {
      setSearchTerm("");
    }

    setIsSearchDropdownOpen(false);
    setHighlightedIndex(-1);
  }, [location.pathname, location.search, queryQ]);

  useEffect(() => {
    if (!showSearchDropdown) {
      setHighlightedIndex(-1);
      return;
    }

    setHighlightedIndex(flattenedSearchOptions.length ? 0 : -1);
  }, [flattenedSearchOptions, showSearchDropdown]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!searchContainerRef.current?.contains(event.target)) {
        setIsSearchFocused(false);
        setIsSearchDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, []);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenPlaygroundMenu = (event) => {
    if (playgroundCloseTimerRef.current) {
      window.clearTimeout(playgroundCloseTimerRef.current);
      playgroundCloseTimerRef.current = null;
    }
    setPlaygroundAnchorEl(event.currentTarget);
  };

  const handleClosePlaygroundMenu = () => {
    if (playgroundCloseTimerRef.current) {
      window.clearTimeout(playgroundCloseTimerRef.current);
      playgroundCloseTimerRef.current = null;
    }
    setPlaygroundAnchorEl(null);
  };

  const handleSchedulePlaygroundMenuClose = () => {
    if (playgroundCloseTimerRef.current) {
      window.clearTimeout(playgroundCloseTimerRef.current);
    }

    playgroundCloseTimerRef.current = window.setTimeout(() => {
      setPlaygroundAnchorEl(null);
      playgroundCloseTimerRef.current = null;
    }, 140);
  };

  const handleKeepPlaygroundMenuOpen = () => {
    if (playgroundCloseTimerRef.current) {
      window.clearTimeout(playgroundCloseTimerRef.current);
      playgroundCloseTimerRef.current = null;
    }
  };

  const handleNavigatePlaygroundItem = (item) => {
    if (item?.disabled || item?.comingSoon) return;
    handleClosePlaygroundMenu();
    guardedNavigate(item.path);
  };

  const handleNavigateSearchResults = ({
    query = searchTerm,
    category = null,
  } = {}) => {
    const trimmed = String(query || "").trim();
    guardedNavigate(buildSearchResultsUrl(trimmed, category), undefined, () => {
      setSearchTerm(trimmed);
      setIsSearchFocused(false);
      setIsSearchDropdownOpen(false);
      setHighlightedIndex(-1);
    });
  };

  const handleSelectSearchOption = (option) => {
    if (!option) return;

    if (option.type !== "all" && option.navigateTo) {
      guardedNavigate(
        option.navigateTo,
        option.routeState ? { state: option.routeState } : undefined,
        () => {
          setIsSearchFocused(false);
          setIsSearchDropdownOpen(false);
          setHighlightedIndex(-1);
        },
      );
      return;
    }

    handleNavigateSearchResults({
      query: option.queryValue,
      category: option.category,
    });
  };

  const handleDismissSearchOption = (event, optionId) => {
    event.preventDefault();
    event.stopPropagation();
    setDismissedSearchOptionIds((currentIds) => {
      const nextIds = new Set(currentIds);
      nextIds.add(optionId);
      return nextIds;
    });
  };

  const handleSearchInputFocus = () => {
    setIsSearchFocused(true);

    if (trimmedSearchTerm.length >= SEARCH_PREVIEW_MIN_QUERY_LENGTH) {
      setIsSearchDropdownOpen(true);
    }
  };

  const handleSearchInputChange = (event) => {
    const nextSearchTerm = event.target.value;
    setSearchTerm(nextSearchTerm);

    if (nextSearchTerm.trim().length >= SEARCH_PREVIEW_MIN_QUERY_LENGTH) {
      setIsSearchDropdownOpen(true);
    } else {
      setIsSearchDropdownOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const handleSearchInputKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      if (!flattenedSearchOptions.length) return;
      event.preventDefault();
      setIsSearchDropdownOpen(true);
      setHighlightedIndex((currentIndex) =>
        currentIndex < 0
          ? 0
          : (currentIndex + 1) % flattenedSearchOptions.length,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      if (!flattenedSearchOptions.length) return;
      event.preventDefault();
      setIsSearchDropdownOpen(true);
      setHighlightedIndex((currentIndex) =>
        currentIndex <= 0
          ? flattenedSearchOptions.length - 1
          : currentIndex - 1,
      );
      return;
    }

    if (event.key === "Escape") {
      setIsSearchDropdownOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const highlightedOption = showSearchDropdown
        ? flattenedSearchOptions[highlightedIndex]
        : null;
      if (highlightedOption) {
        handleSelectSearchOption(highlightedOption);
      } else {
        handleNavigateSearchResults();
      }
    }
  };

  const handleLogout = async () => {
    handleMenuClose();
    setMobileMenuOpen(false);
    runWithGameLeaveGuard(() => {
      dispatch(userLogout());
    });
  };

  const handleOpenMobileMenu = () => {
    setMobileMenuOpen(true);
  };

  const handleCloseMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobilePlaygroundOpen(false);
  };

  const displayName = useMemo(() => {
    const raw = `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`.trim();
    return raw || profile?.username || profile?.email || "User";
  }, [profile]);

  const displayHandle = useMemo(() => {
    const raw =
      profile?.username ||
      profile?.userName ||
      profile?.preferred_username ||
      profile?.handle ||
      profile?.email ||
      "";
    if (!raw) return "";
    const handle = raw.includes("@") ? raw.split("@")[0] : raw;
    return `@${handle.replace(/^@/, "")}`;
  }, [profile]);

  const navItems = NAV_ITEMS;

  const handleNavigateAchievements = () => {
    handleMenuClose();
    handleCloseMobileMenu();
    guardedNavigate("/profile", {
      state: { openAchievements: true },
    });
  };

  const handleNavigateSettings = () => {
    handleMenuClose();
    handleCloseMobileMenu();
    guardedNavigate("/settings");
  };

  const handleNavigateUserProfile = () => {
    handleMenuClose();
    handleCloseMobileMenu();
    guardedNavigate("/profile");
  };

  const handleNavigateFaq = () => {
    handleMenuClose();
    handleCloseMobileMenu();
    guardedNavigate("/FAQs");
  };

  const handleNavigatePrivacy = () => {
    handleMenuClose();
    handleCloseMobileMenu();
    guardedNavigate("/privacy-policy");
  };

  return (
    <>
      <Box className="user-header-row">
        <Box className="user-header-left">
          {navItems.map((item) => {
            const isActive = item.isActive(location.pathname);

            if (item.type === "menu") {
              return (
                <Button
                  key={item.label}
                  onClick={handleOpenPlaygroundMenu}
                  onMouseEnter={handleOpenPlaygroundMenu}
                  onMouseLeave={handleSchedulePlaygroundMenuClose}
                  className={`nav-pill playground-nav-trigger ${
                    item.className || ""
                  } ${isActive ? "active" : ""}`}
                  endIcon={<KeyboardArrowDownRounded />}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(playgroundAnchorEl)}
                  disableRipple
                  disableElevation
                >
                  {item.label}
                </Button>
              );
            }

            return (
              <Button
                key={item.path}
                onClick={() => guardedNavigate(item.path)}
                className={`nav-pill ${item.className || ""} ${isActive ? "active" : ""}`}
                disableRipple
                disableElevation
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        <Box className="user-header-center">
          <Box className="header-search" ref={searchContainerRef}>
            <Box className="header-search__field">
              <FiSearch className="search-icon" aria-hidden="true" />
              <input
                type="text"
                name="kewordal-global-search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="Search lists, keywords, or topics..."
                value={searchTerm}
                onFocus={handleSearchInputFocus}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchInputKeyDown}
                aria-label="Global search"
                role="combobox"
                aria-expanded={showSearchDropdown}
                aria-autocomplete="list"
                aria-controls={showSearchDropdown ? "header-search-results" : undefined}
                aria-activedescendant={
                  showSearchDropdown && !isSearchPreviewLoading && highlightedIndex >= 0
                    ? `header-search-option-${highlightedIndex}`
                    : undefined
                }
              />
            </Box>

            {showSearchDropdown && (
              <Box
                id="header-search-results"
                className="header-search__dropdown"
                role="listbox"
                aria-label="Search suggestions"
              >
                {isSearchPreviewLoading && (
                  <Box className="header-search__state">
                    <CircularProgress size={16} />
                    <span>Searching...</span>
                  </Box>
                )}

                {!isSearchPreviewLoading &&
                  visibleSearchPreviewSections.map((section) => (
                    <Box
                      key={section.key}
                      className="header-search__group"
                      role="group"
                      aria-label={section.title}
                    >
                      {section.items.map((option) => {
                        const optionIndex = searchOptionIndexMap.get(option.id);
                        const isActive = highlightedIndex === optionIndex;

                        return (
                          <Box
                            key={option.id}
                            className="header-search__option-row"
                            role="presentation"
                          >
                            <button
                              id={`header-search-option-${optionIndex}`}
                              type="button"
                              className={`header-search__option ${
                                isActive ? "active" : ""
                              }`}
                              onMouseDown={(event) => event.preventDefault()}
                              onMouseEnter={() =>
                                setHighlightedIndex(optionIndex)
                              }
                              onClick={() => handleSelectSearchOption(option)}
                              role="option"
                              aria-selected={isActive}
                            >
                              <span
                                className={`header-search__option-icon header-search__option-icon--${option.type}`}
                              >
                                {option.type === "person" ? (
                                  <Avatar src={option.avatarURL} alt={option.label} />
                                ) : (
                                  <FormatListBulleted />
                                )}
                              </span>
                              <span className="header-search__option-copy">
                                <span className="header-search__option-label">
                                  {option.label}
                                </span>
                              </span>
                            </button>
                            <button
                              type="button"
                              className="header-search__option-dismiss"
                              aria-label={`Hide ${option.label}`}
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={(event) =>
                                handleDismissSearchOption(event, option.id)
                              }
                            >
                              <Close />
                            </button>
                          </Box>
                        );
                      })}
                    </Box>
                  ))}

                {showNoSearchMatches && (
                  <Box className="header-search__empty">
                    No quick matches found.
                  </Box>
                )}

                {footerSearchOption && (
                  <>
                    <Divider className="header-search__divider" />
                    <button
                      id={`header-search-option-${flattenedSearchOptions.length - 1}`}
                      type="button"
                      role="option"
                      aria-selected={
                        highlightedIndex === flattenedSearchOptions.length - 1
                      }
                      className={`header-search__footer ${
                        highlightedIndex === flattenedSearchOptions.length - 1
                          ? "active"
                          : ""
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseEnter={() =>
                        setHighlightedIndex(flattenedSearchOptions.length - 1)
                      }
                      onClick={() =>
                        handleSelectSearchOption(footerSearchOption)
                      }
                    >
                      {footerSearchOption.label}
                    </button>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Box className="user-header-right">
          <TrialStatusChip
            chip={trial.chip}
            className="trial-status-chip"
            expanded={Boolean(trialPopoverAnchor)}
            onClick={(event) => setTrialPopoverAnchor(event.currentTarget)}
          />
          <Button
            onClick={() => guardedNavigate("/subscriptions")}
            className="upgrade-plan-btn"
            disableElevation
            aria-label="Upgrade Plan"
            startIcon={
              <img
                src={UpgradeIcon}
                alt=""
                aria-hidden="true"
                className="upgrade-plan-icon"
              />
            }
          >
            <span className="upgrade-plan-label">Upgrade Plan</span>
          </Button>

          <IconButton
            type="button"
            className={`global-leaderboard-trigger ${
              location.pathname.startsWith("/global-leaderboard")
                ? "active"
                : ""
            }`}
            onClick={() => guardedNavigate("/global-leaderboard")}
            aria-label="Open global leaderboard"
          >
            <img
              src={LeaderboardIcon}
              alt=""
              aria-hidden="true"
              className="global-leaderboard-icon"
            />
          </IconButton>

          <TrialStatusPopover
            anchorEl={trialPopoverAnchor}
            onClose={() => setTrialPopoverAnchor(null)}
            trial={trial}
            onUpgrade={() => {
              setTrialPopoverAnchor(null);
              guardedNavigate("/subscriptions");
            }}
          />

          {/* Notification popup using Material UI */}
          <NotificationPopover />

          <button
            type="button"
            className="profile-trigger"
            onClick={handleOpenMenu}
            aria-label="Open profile menu"
          >
            <Avatar
              src={profile?.avatarURL}
              alt={profile?.username}
              className="avatar-img"
            />
          </button>

          <IconButton
            className="mobile-menu-button"
            onClick={handleOpenMobileMenu}
            aria-label="Open navigation menu"
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleCloseMobileMenu}
        PaperProps={{ className: "header-mobile-drawer" }}
      >
        <Box className="header-mobile-drawer__content" role="presentation">
          <Box
            className="header-mobile-drawer__profile-card"
            component="button"
            type="button"
            onClick={handleNavigateUserProfile}
          >
            <Avatar
              src={profile?.avatarURL}
              alt={profile?.username}
              className="header-mobile-drawer__profile-avatar"
            />
            <Box className="header-mobile-drawer__profile-copy">
              <Typography className="header-mobile-drawer__title">
                {displayName}
              </Typography>
              {displayHandle ? (
                <Typography className="header-mobile-drawer__subtitle">
                  {displayHandle}
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box className="header-mobile-drawer__section">
            {navItems.map((item) => {
              const isActive = item.isActive(location.pathname);

              if (item.type === "menu") {
                return (
                  <Box key={item.label} className="header-mobile-playground">
                    <button
                      type="button"
                      className={`header-mobile-drawer__link header-mobile-playground__trigger ${
                        isActive ? "active" : ""
                      }`}
                      aria-expanded={mobilePlaygroundOpen}
                      onClick={() =>
                        setMobilePlaygroundOpen((current) => !current)
                      }
                    >
                      <span>Playground</span>
                      <KeyboardArrowDownRounded
                        className={`header-mobile-playground__chevron ${
                          mobilePlaygroundOpen ? "is-open" : ""
                        }`}
                      />
                    </button>
                    {mobilePlaygroundOpen
                      ? PLAYGROUND_ITEMS.map((playgroundItem) => (
                          <button
                            key={playgroundItem.path}
                            type="button"
                            className={`header-mobile-drawer__link header-mobile-playground__link ${
                              location.pathname.startsWith(playgroundItem.path)
                                ? "active"
                                : ""
                            } ${playgroundItem.comingSoon ? "is-coming-soon" : ""}`}
                            disabled={playgroundItem.disabled}
                            aria-disabled={
                              playgroundItem.comingSoon ||
                              playgroundItem.disabled
                            }
                            onClick={() => {
                              if (
                                playgroundItem.disabled ||
                                playgroundItem.comingSoon
                              )
                                return;
                              handleCloseMobileMenu();
                              guardedNavigate(playgroundItem.path);
                            }}
                          >
                            <img
                              src={playgroundItem.icon}
                              alt=""
                              aria-hidden="true"
                            />
                            <span>{playgroundItem.label}</span>
                            {playgroundItem.badge ? (
                              <span className="playground-menu__badge">
                                {playgroundItem.badge}
                              </span>
                            ) : null}
                          </button>
                        ))
                      : null}
                  </Box>
                );
              }

              return (
                <button
                  key={item.path}
                  type="button"
                  className={`header-mobile-drawer__link ${item.className || ""} ${isActive ? "active" : ""}`}
                  onClick={() => {
                    handleCloseMobileMenu();
                    guardedNavigate(item.path);
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </Box>

          <Divider />

          <Box className="header-mobile-drawer__section">
            <button
              type="button"
              className="header-mobile-drawer__cta"
              onClick={() => {
                handleCloseMobileMenu();
                guardedNavigate("/subscriptions");
              }}
            >
              Upgrade Plan
            </button>
          </Box>

          <Divider />

          <Box className="header-mobile-drawer__section">
            <button
              type="button"
              className="header-mobile-drawer__link"
              onClick={handleNavigateAchievements}
            >
              Achievements
            </button>
            <button
              type="button"
              className="header-mobile-drawer__link"
              onClick={handleNavigateSettings}
            >
              Settings
            </button>
            <button
              type="button"
              className="header-mobile-drawer__link"
              onClick={handleNavigateFaq}
            >
              FAQ
            </button>
            <button
              type="button"
              className="header-mobile-drawer__link"
              onClick={handleNavigatePrivacy}
            >
              Privacy Policy
            </button>
            <button
              type="button"
              className="header-mobile-drawer__link header-mobile-drawer__link--danger"
              onClick={handleLogout}
            >
              Logout
            </button>
          </Box>
        </Box>
      </Drawer>

      <MuiMenu
        anchorEl={playgroundAnchorEl}
        open={Boolean(playgroundAnchorEl)}
        onClose={handleClosePlaygroundMenu}
        disableScrollLock
        autoFocus={false}
        disableAutoFocusItem
        className="playground-menu"
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        TransitionProps={{ timeout: 140 }}
        slotProps={{
          paper: {
            onMouseEnter: handleKeepPlaygroundMenuOpen,
            onMouseLeave: handleSchedulePlaygroundMenuClose,
          },
        }}
        MenuListProps={{ "aria-label": "Playground", autoFocusItem: false }}
      >
        {PLAYGROUND_ITEMS.map((item) => (
          <MenuItem
            key={item.path}
            className={`playground-menu__item ${item.comingSoon ? "is-coming-soon" : ""}`}
            disabled={item.disabled}
            aria-disabled={item.comingSoon || item.disabled}
            onClick={() => handleNavigatePlaygroundItem(item)}
          >
            <img src={item.icon} alt="" aria-hidden="true" />
            <span>{item.label}</span>
            {item.badge ? (
              <span className="playground-menu__badge">{item.badge}</span>
            ) : null}
          </MenuItem>
        ))}
      </MuiMenu>

      <MuiMenu
        className="profile-menu"
        id="profile-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{
          "aria-labelledby": "profile-menu",
        }}
        PaperProps={{
          sx: {
            borderRadius: "1rem",
            minWidth: "14.375rem",
            backgroundColor: "#fff",
            boxShadow: "0 0.75rem 1.75rem rgba(0,0,0,0.12)",
            border: "1px solid rgba(0,0,0,0.05)",
            overflow: "hidden",
          },
        }}
      >
        <MenuList disablePadding>
          <Box
            className="profile-menu__header"
            component="button"
            type="button"
            onClick={handleNavigateUserProfile}
          >
            <Typography className="profile-menu__name">
              {displayName}
            </Typography>
            {displayHandle && (
              <Typography className="profile-menu__handle">
                {displayHandle}
              </Typography>
            )}
          </Box>

          <Divider />

          <MenuItem
            onClick={handleNavigateAchievements}
            className="profile-menu__item"
          >
            Achievements
          </MenuItem>
          <MenuItem
            onClick={handleNavigateSettings}
            className="profile-menu__item"
          >
            Settings
          </MenuItem>
          <MenuItem onClick={handleNavigateFaq} className="profile-menu__item">
            FAQs
          </MenuItem>
          <MenuItem
            onClick={handleNavigatePrivacy}
            className="profile-menu__item"
          >
            Privacy Policy
          </MenuItem>

          <Divider />

          <MenuItem
            onClick={handleLogout}
            className="profile-menu__item profile-menu__logout"
          >
            Log out
          </MenuItem>
        </MenuList>
      </MuiMenu>
    </>
  );
};

export default UserHeader;
